/* eslint-disable react/jsx-key */
import React from 'react';
import SVG from 'react-inlinesvg';
import PT from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import handIcon from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import personLockIcon from '@lifesize/ux-assets/vectors/svgIcons/person-lock.svg';
import micIcon from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import micOffIcon from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import ContactCardHeader from 'components/Contacts/ContactCardHeader';
import Tooltip from 'components/Tooltip/Tooltip';
import InCallParticipant from 'components/InCall/InCallParticipantList/InCallParticipant';
import { participantFilterType } from 'constants/ParticipantList';
import classes from './RemoteModeratorPanel.module.scss';

export class RemoteModerationPanel extends React.PureComponent {
  handleFilterNone = () => {
    this.props.selectParticipantFilter(participantFilterType.none);
  };

  handleFilterHandsRaised = () => {
    this.props.selectParticipantFilter(participantFilterType.raisedHands);
  };

  handleFilterUnmuted = () => {
    this.props.selectParticipantFilter(participantFilterType.unmuted);
  };

  preventClickThrough = (evt) => {
    evt.stopPropagation();
  };

  render() {
    const {
      canMuteAll,
      isAudioMutedByServer,
      isMeetingLocked,
      lecturer,
      moderators,
      onMuteAllClicked,
      participantFilter,
      // participantsCount,
      raisedHandsCount,
      remoteModerators,
      showLowerAllHandsButton,
      waitingRoomFeatureEnabled
    } = this.props;

    return (
      <div className={classes.background} onClick={this.props.handleConfirmRemoteModerationEnd}>
        <div className={classes.container} onClick={this.preventClickThrough}>
          <ContactCardHeader
            contact={this.props.contact}
            handleRemoteModerate={this.props.handleRemoteModerate}
            remoteModerationIsActiveForThisMeeting={true}
            scroll={0}
            userCanRemoteModerate={true}
          />
          {showLowerAllHandsButton && (
            <div className={classes.filterContainer}>
              <button
                data-testid="allParticipants"
                disabled={participantFilter === participantFilterType.none}
                onClick={this.handleFilterNone}
              >
                <FormattedMessage id="components.inCall.participantList.filter.all" defaultMessage="All" />
              </button>
              <button
                data-testid="handRaisedParticipants"
                disabled={participantFilter === participantFilterType.raisedHands}
                onClick={this.handleFilterHandsRaised}
              >
                <SVG src={handIcon} className={!!raisedHandsCount ? classes.raisedHands : ''} />
                <FormattedMessage
                  id="components.inCall.participantList.filter.raisedHands"
                  defaultMessage="{handsRaised, number} raised {handsRaised, plural, one {hand} other {hands}}"
                  values={{ handsRaised: raisedHandsCount }}
                />
              </button>
              <button
                data-testid="unmutedParticipants"
                disabled={participantFilter === participantFilterType.unmuted}
                onClick={this.handleFilterUnmuted}
              >
                <SVG src={micIcon} data-testid="micIcon" />
                <FormattedMessage
                  id="components.inCall.participantList.filter.unmuted"
                  defaultMessage="{unmutedParticipants, number} unmuted"
                  values={{ unmutedParticipants: this.props.unmutedCount }}
                />
              </button>
            </div>
          )}
          <div className={classes.participantList}>
            {this.props.participants.map((participant, index) => (
              <InCallParticipant
                canBeMuted={canMuteAll}
                key={`participant-${participant.participantId}-${index}`}
                onLowerHandClicked={this.props.onLowerHandClicked}
                onMuteParticipantClicked={this.props.onMuteParticipantClicked}
                onRemoveFromMeetingClicked={this.props.removeFromMeetingClicked}
                onSelectRaisedHandClicked={this.props.onSelectRaisedHandClicked}
                onStopPresenterClicked={this.props.onStopPresenterClicked}
                userIsModerator={true}
                participant={participant}
              />
            ))}
            {participantFilter === participantFilterType.none && (
              <div className={classes.participantRoles}>
                {moderators && moderators.length > 0 && (
                  <div>
                    <FormattedMessage
                      id="components.inCall.participantList.moderatorLabel"
                      description="List of meeting moderators"
                      defaultMessage={'Moderators:'}
                    />
                    {moderators.map((m, index) => (
                      <p data-testid="moderator" key={`moderator-${index}`}>
                        {m.displayName}
                      </p>
                    ))}
                  </div>
                )}
                {remoteModerators.length > 0 && (
                  <div>
                    <FormattedMessage
                      id="components.inCall.participantList.remoteModeratorLabel"
                      defaultMessage={'Remote Moderators:'}
                    />
                    {remoteModerators.map((rm, index) => (
                      <p data-testid="remoteModerator" key={`remoteModerator-${index}`}>
                        {rm.displayName}
                      </p>
                    ))}
                  </div>
                )}
                {!!lecturer && (
                  <div data-testid="lecturer">
                    <FormattedMessage
                      id="components.inCall.participantList.lecturerLabel"
                      description="In call participant list lecturer label"
                      defaultMessage={'Lecturer: {name}'}
                      values={{ name: lecturer.name }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={classes.buttonContainer}>
            {canMuteAll && !isAudioMutedByServer && (
              <button
                data-testid="btnMuteAll"
                key="muteAll"
                onClick={() => {
                  onMuteAllClicked(true);
                }}
              >
                <div className={classes.tooltipWrapper}>
                  <Tooltip
                    location={'top'}
                    overlapCarat={false}
                    label={
                      <FormattedMessage
                        id="contacts.tooltip.muteAll"
                        defaultMessage="Participants muted by the moderator cannot unmute themselves."
                      />
                    }
                  />
                </div>
                <SVG src={micOffIcon} data-testid="micOffIcon" className={classes.buttonIcon} />
                <FormattedMessage id="components.inCall.mute" defaultMessage="Mute All" />
              </button>
            )}
            {canMuteAll && isAudioMutedByServer && (
              <button
                data-testid="btnUnmuteAll"
                key="unmuteAll"
                onClick={() => {
                  onMuteAllClicked(false);
                }}
              >
                <div className={classes.tooltipWrapper}>
                  <Tooltip
                    location={'top'}
                    overlapCarat={false}
                    label={
                      <FormattedMessage
                        id="contacts.tooltip.unmuteAll"
                        defaultMessage="Unmute participants. Self-muted participants will remain muted."
                      />
                    }
                  />
                </div>
                <SVG src={micIcon} data-testid="micIcon" className={classes.buttonIcon} />
                <FormattedMessage id="components.inCall.unMute" defaultMessage="Unmute All" />
              </button>
            )}
            {showLowerAllHandsButton && (
              <button
                data-testid="btnLowerAllHands"
                disabled={!raisedHandsCount}
                onClick={this.props.onLowerAllHandsClicked}
              >
                <SVG src={handIcon} className={classes.buttonIcon} />
                <FormattedMessage id="components.inCall.lowerAllHands" defaultMessage="Lower All Hands" />
              </button>
            )}
            {!waitingRoomFeatureEnabled && (
              <button
                data-testid={isMeetingLocked ? 'btnUnlockMeeting' : 'btnLockMeeting'}
                onClick={() => this.props.onLockMeetingClicked(isMeetingLocked)}
              >
                <SVG src={personLockIcon} data-testid="personLockIcon" className={classes.buttonIcon} />
                {isMeetingLocked ? (
                  <FormattedMessage id="components.inCall.unlockMeeting" defaultMessage="Unlock this Meeting" />
                ) : (
                  <FormattedMessage id="components.inCall.lockMeeting" defaultMessage="Lock this Meeting" />
                )}
              </button>
            )}
            {this.props.canEndMeetingForAll && (
              <button data-testid="btnStopRemoteModeration" onClick={this.props.onEndMeetingForAll}>
                <SVG src={cancelIcon} data-testid="cancelIcon" className={classes.buttonIcon} />
                <FormattedMessage id="components.inCall.endMeetingForAll" defaultMessage="End Meeting for All" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

RemoteModerationPanel.propTypes = {
  canChangeMeetingLock: PT.bool,
  canEndMeetingForAll: PT.bool,
  canMuteAll: PT.bool,
  contact: PT.object.isRequired, // ownProps
  handleConfirmRemoteModerationEnd: PT.func.isRequired,
  handleRemoteModerate: PT.func, // ownProps
  isAudioMutedByServer: PT.bool,
  isMeetingLocked: PT.bool,
  lecturer: PT.object,
  moderators: PT.array,
  onEndMeetingForAll: PT.func.isRequired,
  onLockMeetingClicked: PT.func.isRequired,
  onLowerAllHandsClicked: PT.func.isRequired,
  onLowerHandClicked: PT.func.isRequired,
  onMuteAllClicked: PT.func.isRequired,
  onMuteParticipantClicked: PT.func.isRequired,
  onSelectRaisedHandClicked: PT.func.isRequired,
  onStopPresenterClicked: PT.func.isRequired,
  participantFilter: PT.string, // ownProps
  participants: PT.array,
  participantsCount: PT.number,
  raisedHandsCount: PT.number,
  remoteModerators: PT.array.isRequired,
  removeFromMeetingClicked: PT.func,
  selectParticipantFilter: PT.func.isRequired, // ownProps
  showLowerAllHandsButton: PT.bool,
  unmutedCount: PT.number,
  waitingRoomFeatureEnabled: PT.bool
};

export default injectIntl(RemoteModerationPanel);
